import { BlocksRenderer, type BlocksContent } from '@strapi/blocks-react-renderer';
import type { ReactNode } from 'react';

/**
 * Renders Strapi rich-text blocks with the site's type scale.
 *
 * Styling lives here rather than in a Tailwind typography plugin so headings
 * inside CMS copy pick up the display face and the same optical-size handling
 * as the rest of the page.
 */
export function Prose({
  content,
  tone = 'default',
  size = 'default',
  className = '',
}: {
  content?: BlocksContent | null;
  tone?: 'default' | 'inverse';
  size?: 'default' | 'lead';
  className?: string;
}) {
  if (!content?.length) return null;

  const body = tone === 'inverse' ? 'text-sapphire/80' : 'text-sapphire-70';
  const strong = tone === 'inverse' ? 'text-sapphire' : 'text-sapphire';
  const text = size === 'lead' ? 'text-lead' : 'text-base leading-relaxed';

  return (
    <div className={`${body} ${text} space-y-5 ${className}`}>
      <BlocksRenderer
        content={content}
        blocks={{
          paragraph: ({ children }) => <p className="max-w-measure">{children}</p>,
          heading: ({ children, level }) => {
            const sizes = {
              1: 'text-display-md',
              2: 'text-display-sm',
              3: 'text-2xl',
              4: 'text-xl',
              5: 'text-lg',
              6: 'text-base',
            } as const;
            const Tag = `h${level}` as 'h1';
            return <Tag className={`${sizes[level]} ${strong} mt-10 first:mt-0`}>{children}</Tag>;
          },
          list: ({ children, format }) =>
            format === 'ordered' ? (
              <ol className="max-w-measure list-decimal space-y-2 pl-5 marker:text-carnelian">
                {children}
              </ol>
            ) : (
              <ul className="max-w-measure list-disc space-y-2 pl-5 marker:text-carnelian">
                {children}
              </ul>
            ),
          'list-item': ({ children }) => <li className="pl-1">{children}</li>,
          quote: ({ children }) => (
            <blockquote
              className={`max-w-measure border-l-2 border-carnelian pl-6 font-display text-xl italic ${strong}`}
            >
              {children}
            </blockquote>
          ),
          link: ({ children, url }) => (
            <a
              href={url}
              className="underline decoration-carnelian decoration-2 underline-offset-[5px] transition-colors duration-fast hover:text-carnelian"
              {...(url.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {children}
            </a>
          ),
          image: ({ image }) => (
            <img
              src={image.url}
              alt={image.alternativeText ?? ''}
              width={image.width}
              height={image.height}
              loading="lazy"
              decoding="async"
              className="my-8 w-full"
            />
          ),
          code: ({ children }) => (
            <pre className="overflow-x-auto bg-glass-deep p-4 text-sm">
              <code>{children}</code>
            </pre>
          ),
        }}
        modifiers={{
          bold: ({ children }) => <strong className={`font-semibold ${strong}`}>{children}</strong>,
          italic: ({ children }) => <em>{children}</em>,
          underline: ({ children }) => <u>{children}</u>,
          strikethrough: ({ children }) => <s>{children}</s>,
          code: ({ children }) => (
            <code className="bg-glass-deep px-1.5 py-0.5 text-[0.9em]">{children}</code>
          ),
        }}
      />
    </div>
  );
}

/**
 * Prose with a plain-text fallback.
 *
 * Most CMS fields are optional and half the entries predate the redesign, so
 * components pass both the rich-text field and a sensible string default.
 */
export function ProseOr({
  content,
  fallback,
  tone = 'default',
  size = 'default',
  className = '',
}: {
  content?: BlocksContent | null;
  fallback: ReactNode;
  tone?: 'default' | 'inverse';
  size?: 'default' | 'lead';
  className?: string;
}) {
  if (content?.length) {
    return <Prose content={content} tone={tone} size={size} className={className} />;
  }

  const body = tone === 'inverse' ? 'text-sapphire/80' : 'text-sapphire-70';
  const text = size === 'lead' ? 'text-lead' : 'text-base leading-relaxed';
  return <p className={`max-w-measure ${body} ${text} ${className}`}>{fallback}</p>;
}
